/** Auth endpoints (handoff §4). */
import { z } from "zod";
import { apiGet, apiPost } from "@shared/api";
import {
  loginResponseSchema,
  userSchema,
  type AuthUser,
  type LoginResponse,
} from "./auth.schemas";

export const login = (
  email: string,
  password: string,
  recaptchaToken?: string,
): Promise<LoginResponse> =>
  apiPost(loginResponseSchema, "/auth/login", {
    email,
    password,
    ...(recaptchaToken ? { recaptcha_token: recaptchaToken } : {}),
  });

export const fetchMe = (): Promise<AuthUser> => apiGet(userSchema, "/auth/me");

/**
 * Server-side logout. Note (handoff §7): this does NOT revoke the token — it
 * stays valid until it expires. Dropping our copy is what actually logs out.
 */
export const logout = (): Promise<unknown> =>
  apiPost(z.unknown(), "/auth/logout", {});

/**
 * Fire-and-forget logout for tab-close / pagehide events (Sep-3).
 *
 * The regular `logout()` above uses axios, which is aborted the moment the
 * page unloads — meaning a user who just closes the tab never actually
 * signs out server-side. That's what causes `session_id` to get stuck on
 * the user document and trigger "Already logged in on another session"
 * on the next login.
 *
 * This variant uses `fetch(keepalive: true)`, which browsers guarantee
 * will complete even after the page unloads (up to 64 KB body, plenty for
 * a signout). `navigator.sendBeacon` would be the classic choice, but it
 * can't set custom headers — we need the Authorization header to identify
 * whose session to clear.
 *
 * Non-throwing by design — the page is going away regardless, and there's
 * no UI to surface an error to.
 */
export function logoutBeacon(): void {
  // Pure sync read of the token — avoids importing getToken to keep this
  // path minimal at unload time. The key is defined once in shared/api/token.ts.
  const raw = (() => {
    try { return localStorage.getItem("specta.token"); } catch { return null; }
  })();
  if (!raw) return;

  const base = (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";
  const url = `${base}/auth/logout`;
  try {
    // `keepalive: true` is the whole point — without it, the browser
    // cancels the request when the page unloads and the server never
    // receives it. `.catch` guards against transient rejections.
    void fetch(url, {
      method: "POST",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${raw}`,
      },
      body: "{}",
    }).catch(() => { /* fire and forget — page is unloading */ });
  } catch {
    /* synchronous throw is very rare; page is closing anyway */
  }
}

/* ------------------------------------------------------------------
 * Password reset — 3-step OTP flow (PASSWORD_RESET_HANDOFF.md).
 *
 * All three endpoints are PUBLIC: no Authorization header. The user is
 * locked out, so they cannot have a token.
 *
 * State flows: otp_token (step 1 -> 2), reset_token (step 2 -> 3).
 * OTP: 6 digits, 10 min, 5 attempts. reset_token: 15 min, single use.
 * ------------------------------------------------------------------ */

const forgotPasswordSchema = z
  .object({
    otp_token: z.string(),
    masked_email: z.string(),
    message: z.string().optional(),
    /** Only present when the server has no SMTP configured (dev only). */
    dev_otp: z.string().optional(),
  })
  .passthrough();

const verifyOtpSchema = z
  .object({ reset_token: z.string(), message: z.string().optional() })
  .passthrough();

const resetPasswordSchema = z
  .object({ message: z.string().optional() })
  .passthrough();

export const forgotPassword = (email: string) =>
  apiPost(forgotPasswordSchema, "/auth/forgot-password", { email });

export const verifyOTP = (otpToken: string, otpCode: string) =>
  apiPost(verifyOtpSchema, "/auth/verify-otp", { otp_token: otpToken, otp_code: otpCode });

export const resetPassword = (
  resetToken: string,
  newPassword: string,
  confirmPassword: string,
) =>
  apiPost(resetPasswordSchema, "/auth/reset-password", {
    reset_token: resetToken,
    new_password: newPassword,
    confirm_password: confirmPassword,
  });
